import { Button, Empty, Input, Skeleton, Table, Tag } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { getOverviewTeamDirectory } from '../../api'
import { formatDateTime as formatDateTimeI18n } from '../../../i18n/dateFormat'
import { useUiLocale } from '../../../i18n/LocaleProvider'
import type { OverviewTeamDirectory, OverviewTeamDirectoryMember } from '../../types'

export default function TeamDirectoryTab({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation(['overview'])
  const { locale } = useUiLocale()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<OverviewTeamDirectory | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(q.trim()), 250)
    return () => window.clearTimeout(id)
  }, [q])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getOverviewTeamDirectory(workspaceId, debouncedQ ? { q: debouncedQ } : undefined)
      .then((r) => {
        if (!cancelled) setData(r.data)
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null)
          setError(err?.message || t('overview:tabs.v3.team.loadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId, debouncedQ, t])

  if (loading && !data) {
    return <Skeleton active paragraph={{ rows: 6 }} />
  }

  if (error) {
    return <Empty description={error} />
  }

  if (!data?.enabled || !data.team) {
    return <Empty description={t('overview:tabs.v3.team.notTeam')} />
  }

  const team = data.team

  return (
    <div className="tc-ov-v3-team" data-testid="overview-team-tab">
      <div className="tc-data-card tc-ov-v3-card">
        <div className="tc-ov-v3-team-info">
          <div>
            <div className="tc-ov-v2-profile-name">{team.name}</div>
            <div className="tc-ov-v2-profile-email">
              {t('overview:tabs.v3.team.size', { count: team.active_member_count })} ·{' '}
              {t('overview:tabs.v3.team.myRole')}: {team.my_role}
              {team.my_joined_at ? ` · ${formatDateTimeI18n(team.my_joined_at, locale)}` : ''}
            </div>
          </div>
          {team.can_manage_team ? (
            <Button type="primary" data-testid="overview-goto-team-mgmt" onClick={() => navigate(`/workspace/${workspaceId}/team`)}>
              {t('overview:tabs.v3.team.openManagement')}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="tc-data-card tc-ov-v3-card">
        <div className="tc-ov-panel-head">
          <span className="tc-ov-panel-title">{t('overview:tabs.v3.team.membersTitle')}</span>
          <Input.Search
            allowClear
            placeholder={t('overview:tabs.v3.team.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ maxWidth: 260 }}
            data-testid="overview-team-search"
          />
        </div>
        <Table
          rowKey="user_id"
          size="small"
          loading={loading}
          dataSource={data.members}
          pagination={false}
          locale={{ emptyText: <Empty description={t('overview:tabs.v3.team.emptyMembers')} /> }}
          columns={[
            {
              title: t('overview:tabs.v3.team.col.member'),
              render: (_: unknown, row: OverviewTeamDirectoryMember) => (
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {row.display_name}
                    {row.is_self ? ` (${t('overview:tabs.v3.team.self')})` : ''}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{row.email}</div>
                </div>
              ),
            },
            {
              title: t('overview:tabs.v3.team.col.role'),
              dataIndex: 'role',
              width: 120,
            },
            {
              title: t('overview:tabs.v3.team.col.joined'),
              dataIndex: 'joined_at',
              width: 160,
              render: (v: string | null) => (v ? formatDateTimeI18n(v, locale) : '—'),
            },
            {
              title: t('overview:tabs.v3.team.col.status'),
              dataIndex: 'status',
              width: 100,
              render: (v: string) => <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag>,
            },
          ]}
        />
      </div>
    </div>
  )
}
